import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ActivityLogPanel = ({ logs, onRefresh }) => {
  const [filterAction, setFilterAction] = useState('all');

  const getActionIcon = (action) => {
    const icons = {
      login: 'LogIn',
      logout: 'LogOut',
      create: 'Plus',
      update: 'Edit',
      delete: 'Trash2',
      invite: 'UserPlus',
      role_change: 'Shield',
      permission_change: 'Key'
    };
    return icons?.[action] || 'Activity';
  };

  const getActionColor = (action) => {
    const colors = {
      login: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30',
      logout: 'text-gray-600 bg-gray-100 dark:bg-gray-900/30',
      create: 'text-success bg-success/10',
      update: 'text-accent bg-accent/10',
      delete: 'text-error bg-error/10',
      invite: 'text-purple-600 bg-purple-100 dark:bg-purple-900/30',
      role_change: 'text-warning bg-warning/10',
      permission_change: 'text-orange-600 bg-orange-100 dark:bg-orange-900/30'
    };
    return colors?.[action] || 'text-muted-foreground bg-muted';
  };

  const formatAction = (action) =>
    action?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const actionFilterOptions = [
    { value: 'all', label: 'All Actions' },
    { value: 'login', label: 'Login' },
    { value: 'create', label: 'Create' },
    { value: 'update', label: 'Update' },
    { value: 'delete', label: 'Delete' },
    { value: 'invite', label: 'Invite' },
    { value: 'role_change', label: 'Role Change' }
  ];

  const filteredLogs = filterAction === 'all'
    ? logs
    : logs?.filter(log => log?.action === filterAction);

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className="bg-card rounded-xl p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Activity History</h3>
          <div className="flex items-center gap-2">
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e?.target?.value)}
              className="px-3 py-1.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {actionFilterOptions?.map(opt => (
                <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
              ))}
            </select>
            {onRefresh && (
              <button
                onClick={onRefresh}
                className="p-1.5 hover:bg-muted rounded-md transition-colors"
                title="Refresh logs"
              >
                <Icon name="RefreshCw" size={16} className="text-muted-foreground" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-card rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 md:p-6">
          {!filteredLogs?.length ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <Icon name="Activity" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No activity logs</h3>
              <p className="text-sm text-muted-foreground">
                {filterAction !== 'all' ? 'No logs match the selected filter.' : 'Activity logs will appear here as team members perform actions.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredLogs?.map((log) => (
                <div
                  key={log?.id}
                  className="flex items-start gap-4 p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors"
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${getActionColor(log?.action)}`}>
                    <Icon name={getActionIcon(log?.action)} size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm font-medium text-foreground">
                        {log?.userProfiles?.fullName || log?.user_profiles?.full_name || 'Unknown User'}
                      </p>
                      <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                        {formatDateTime(log?.createdAt)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getActionColor(log?.action)}`}>
                        {formatAction(log?.action)}
                      </span>
                      <span className="text-xs text-muted-foreground capitalize">{log?.resourceType}</span>
                    </div>
                    {log?.details && typeof log?.details === 'object' && Object.keys(log?.details)?.length > 0 && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {log?.details?.message || log?.details?.invited_email
                          ? (log?.details?.message || `Invited ${log?.details?.invited_email}`)
                          : null
                        }
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityLogPanel;